/**
 * m3 echo Frontend - Create Project Dialog Component
 *
 * Dialog für die Erstellung neuer Projekte (mit erstem Job).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { promptsApi } from '../services/api';
import { Prompt } from '../types/api';
import { useCurrentBenchmarkModels } from '../hooks/useModelCatalog';
import { useUIStore } from '../stores/useUIStore';
import { useCreateProject } from '../hooks/useProjects';
import { useAuthStore } from '../stores/useAuthStore';
import { usePromptQualityWarning } from '../hooks/usePromptQualityWarning';

const CreateProjectDialog: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Use Zustand store for dialog state
  const { createProjectDialogOpen, closeCreateProjectDialog } = useUIStore();

  // Get user and tenant info for API calls
  const user = useAuthStore((state) => state.user);
  const tenant = useAuthStore((state) => state.tenant);
  const isAdmin = user?.is_admin || false;
  // Zielmandant: bei Admins der ausgewählte Mandant, sonst der eigene (Backend)
  const targetTenantId = isAdmin && tenant?.id ? tenant.id : undefined;

  // Use React Query mutation
  const createProjectMutation = useCreateProject();
  const {
    data: benchmarkModels = [],
    isLoading: benchmarkLoading,
    isError: benchmarkError,
  } = useCurrentBenchmarkModels(targetTenantId);
  const [activeStep, setActiveStep] = useState(0);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [selectedPrompts, setSelectedPrompts] = useState<Prompt[]>([]);
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermSynonym1, setSearchTermSynonym1] = useState('');  // v4.8.0
  const [searchTermSynonym2, setSearchTermSynonym2] = useState('');  // v4.8.0
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for inline prompt creation
  const [showCreatePromptDialog, setShowCreatePromptDialog] = useState(false);
  const [newPromptContent, setNewPromptContent] = useState('');
  const [creatingPrompt, setCreatingPrompt] = useState(false);
  const newPromptWarning = usePromptQualityWarning(newPromptContent);

  const steps = ['Projekt-Info', 'Prompts auswählen', 'Parameter eingeben', 'Zusammenfassung'];

  /**
   * Lädt Prompts beim Öffnen des Dialogs
   */
  useEffect(() => {
    if (createProjectDialogOpen) {
      loadPrompts();
      // Reset state when dialog opens
      resetState();
    }
  }, [createProjectDialogOpen]);

  /**
   * Lädt alle verfügbaren Prompts
   */
  const loadPrompts = async () => {
    try {
      // Check if admin user needs a tenant
      if (isAdmin && !tenant?.id) {
        setError('Als Admin-Benutzer müssen Sie einem Mandanten zugewiesen sein, um Projekte zu erstellen.');
        return;
      }

      // For admin users, use tenant_id; for regular users, backend uses their tenant
      const params = isAdmin && tenant?.id ? { tenant_id: tenant.id } : {};
      const response = await promptsApi.getAll(params);
      setPrompts(response.data);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Fehler beim Laden der Prompts:', error);
      setError('Prompts konnten nicht geladen werden.');
    }
  };

  /**
   * Handler für inline Prompt-Erstellung
   */
  const handleCreatePromptInline = async () => {
    if (!newPromptContent.trim()) return;

    setCreatingPrompt(true);
    try {
      // Get tenant_id for the API call
      const tenantId = isAdmin && tenant?.id ? tenant.id : undefined;
      const response = await promptsApi.create({ content: newPromptContent }, tenantId);
      const createdPrompt = response.data;

      // Add to local prompts list
      setPrompts((prev) => [...prev, createdPrompt]);

      // Auto-select the new prompt
      handlePromptToggle(createdPrompt);

      // Reset and close dialog
      setNewPromptContent('');
      setShowCreatePromptDialog(false);
    } catch (error) {
      console.error('Fehler beim Erstellen des Prompts:', error);
      setError('Prompt konnte nicht erstellt werden');
    } finally {
      setCreatingPrompt(false);
    }
  };

  /**
   * Behandelt die Prompt-Auswahl (Multi-Select)
   */
  const handlePromptToggle = (prompt: Prompt) => {
    setSelectedPrompts((prev) => {
      const isSelected = prev.some((p) => p.id === prompt.id);
      const newSelection = isSelected
        ? prev.filter((p) => p.id !== prompt.id)
        : [...prev, prompt];

      // Platzhalter-Werte für alle ausgewählten Prompts sammeln
      const allPlaceholders = new Set<string>();
      newSelection.forEach((p) => {
        p.placeholders.forEach((ph) => allPlaceholders.add(ph));
      });

      // Platzhalter-Werte initialisieren (bestehende Werte beibehalten)
      const initialValues: Record<string, string> = { ...placeholderValues };
      allPlaceholders.forEach((placeholder) => {
        if (!(placeholder in initialValues)) {
          initialValues[placeholder] = '';
        }
      });
      setPlaceholderValues(initialValues);

      return newSelection;
    });
  };

  /**
   * Prüft, ob der nächste Schritt möglich ist
   */
  const canProceed = (): boolean => {
    switch (activeStep) {
      case 0: // Project info
        return projectName.trim().length > 0;
      case 1: // Prompt selection
        return selectedPrompts.length > 0;
      case 2: // Placeholder values
        const allPlaceholders = new Set<string>();
        selectedPrompts.forEach((p) => {
          p.placeholders.forEach((ph) => allPlaceholders.add(ph));
        });
        const allFilled = Array.from(allPlaceholders).every(
          (ph) => placeholderValues[ph]?.trim().length > 0
        );
        return allFilled && searchTerm.trim().length > 0;
      case 3: // Summary - always allow proceeding (data already validated)
        return true;
      default:
        return false;
    }
  };

  /**
   * Geht zum nächsten Schritt
   */
  const handleNext = () => {
    if (canProceed()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  /**
   * Geht zum vorherigen Schritt
   */
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  /**
   * Erstellt das Projekt
   */
  const handleSubmit = async () => {
    if (!canProceed()) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare prompts data
      const promptsData = selectedPrompts.map((prompt) => {
        const values: Record<string, string> = {};
        prompt.placeholders.forEach((ph) => {
          values[ph] = placeholderValues[ph] || '';
        });
        return {
          prompt_id: prompt.id,
          prompt_values: values,
        };
      });

      // Create project using mutation
      // For admin users, pass the selected tenant_id
      await createProjectMutation.mutateAsync({
        data: {
          name: projectName,
          description: projectDescription || undefined,
          prompts: promptsData,
          search_term: searchTerm,
          search_term_synonym1: searchTermSynonym1.trim() || undefined,  // v4.8.0
          search_term_synonym2: searchTermSynonym2.trim() || undefined,  // v4.8.0
        },
        tenant_id: targetTenantId,
      });

      // Success - mutation will invalidate queries automatically
      closeCreateProjectDialog();
      resetState();
    } catch (error: any) {
      console.error('Fehler beim Erstellen des Projekts:', error);
      const errorMessage = error?.response?.data?.detail || 'Projekt konnte nicht erstellt werden';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Setzt alle Eingaben zurück
   */
  const resetState = () => {
    setActiveStep(0);
    setProjectName('');
    setProjectDescription('');
    setSelectedPrompts([]);
    setPlaceholderValues({});
    setSearchTerm('');
    setSearchTermSynonym1('');  // v4.8.0
    setSearchTermSynonym2('');  // v4.8.0
    setError(null);
    setShowCreatePromptDialog(false);
    setNewPromptContent('');
  };

  /**
   * Schließt den Dialog
   */
  const handleClose = () => {
    if (!loading) {
      closeCreateProjectDialog();
      // Reset nach kurzer Verzögerung (damit Übergang smooth ist)
      setTimeout(resetState, 300);
    }
  };

  /**
   * Rendert den Inhalt für den aktuellen Schritt
   */
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Geben Sie einen Namen und optional eine Beschreibung für Ihr Projekt ein.
              Das Projekt wird alle Jobs und Re-Tests mit identischen Einstellungen gruppieren.
            </Typography>
            <TextField
              fullWidth
              label="Projekt-Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="z.B. Stadtwerke Bonn Q1 2025"
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Beschreibung (optional)"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              placeholder="z.B. Monatliche Tracking-Kampagne für Q1"
              multiline
              rows={3}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Wählen Sie einen oder mehrere Prompts aus. Diese werden für alle Jobs in diesem Projekt verwendet.
            </Typography>

            {/* Button zum Erstellen eines neuen Prompts */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowCreatePromptDialog(true)}
              >
                + Neuen Prompt erstellen
              </Button>
            </Box>

            {prompts.length === 0 ? (
              <Alert severity="info">
                Keine Prompts verfügbar. Erstellen Sie einen neuen Prompt mit dem Button oben.
              </Alert>
            ) : (
              <FormControl component="fieldset">
                <FormGroup>
                  {prompts.map((prompt) => (
                    <FormControlLabel
                      key={prompt.id}
                      control={
                        <Checkbox
                          checked={selectedPrompts.some((p) => p.id === prompt.id)}
                          onChange={() => handlePromptToggle(prompt)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{prompt.content}</Typography>
                          {prompt.placeholders.length > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              Platzhalter: {prompt.placeholders.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}

            {/* Inline Prompt Creation Dialog */}
            <Dialog
              open={showCreatePromptDialog}
              onClose={() => !creatingPrompt && setShowCreatePromptDialog(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Neuen Prompt erstellen</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Prompt-Inhalt"
                  fullWidth
                  multiline
                  rows={4}
                  value={newPromptContent}
                  onChange={(e) => setNewPromptContent(e.target.value.slice(0, 300))}
                  helperText={`Verwenden Sie [platzhalter] für dynamische Werte, z.B. [Ort] oder [Branche] (${newPromptContent.length}/300 Zeichen)`}
                  disabled={creatingPrompt}
                  error={newPromptContent.length >= 300}
                  inputProps={{ maxLength: 300 }}
                />
                <Collapse in={!!newPromptWarning}>
                  <Alert severity="warning" sx={{ mt: 1 }}>{newPromptWarning}</Alert>
                </Collapse>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowCreatePromptDialog(false)} disabled={creatingPrompt}>
                  Abbrechen
                </Button>
                <Button
                  onClick={handleCreatePromptInline}
                  variant="contained"
                  disabled={!newPromptContent.trim() || creatingPrompt || newPromptContent.length > 500}
                >
                  {creatingPrompt ? 'Wird erstellt...' : 'Erstellen'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 2:
        const allPlaceholders = new Set<string>();
        selectedPrompts.forEach((p) => {
          p.placeholders.forEach((ph) => allPlaceholders.add(ph));
        });

        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Füllen Sie die Platzhalter aus und geben Sie den Suchbegriff ein.
              Diese Einstellungen gelten für alle Jobs in diesem Projekt.
            </Typography>

            {/* Platzhalter-Felder */}
            {Array.from(allPlaceholders).map((placeholder) => {
              const value = placeholderValues[placeholder] || '';
              return (
                <TextField
                  key={placeholder}
                  fullWidth
                  label={placeholder}
                  value={value}
                  onChange={(e) =>
                    setPlaceholderValues({ ...placeholderValues, [placeholder]: e.target.value.slice(0, 50) })
                  }
                  error={value.length > 50}
                  helperText={`${value.length}/50 Zeichen`}
                  inputProps={{ maxLength: 50 }}
                  sx={{ mb: 2 }}
                  required
                />
              );
            })}

            {/* Suchbegriff */}
            <TextField
              fullWidth
              label="Suchbegriff"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.slice(0, 50))}
              placeholder="z.B. Stadtwerke Bonn"
              required
              error={searchTerm.length > 50}
              helperText={`Der Begriff, nach dem in den KI-Antworten gesucht werden soll (${searchTerm.length}/50 Zeichen)`}
              inputProps={{ maxLength: 50 }}
              sx={{ mb: 2 }}
            />

            {/* Alternative Suchbegriffe (v4.8.0) */}
            <TextField
              fullWidth
              label="Alternativer Suchbegriff 1 (optional)"
              value={searchTermSynonym1}
              onChange={(e) => setSearchTermSynonym1(e.target.value.slice(0, 50))}
              placeholder="z.B. O2"
              error={searchTermSynonym1.length > 50}
              helperText={`Synonym oder alternativer Name (${searchTermSynonym1.length}/50 Zeichen)`}
              inputProps={{ maxLength: 50 }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Alternativer Suchbegriff 2 (optional)"
              value={searchTermSynonym2}
              onChange={(e) => setSearchTermSynonym2(e.target.value.slice(0, 50))}
              placeholder="z.B. E-Plus"
              error={searchTermSynonym2.length > 50}
              helperText={`Weiteres Synonym oder alternativer Name (${searchTermSynonym2.length}/50 Zeichen)`}
              inputProps={{ maxLength: 50 }}
            />
          </Box>
        );

      case 3:
        // Gather all placeholders for display
        const summaryPlaceholders = new Set<string>();
        selectedPrompts.forEach((p) => {
          p.placeholders.forEach((ph) => summaryPlaceholders.add(ph));
        });

        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              Projekteinstellungen (Prompts, Parameter und Suchbegriffe) können nach Erstellung nicht mehr angepasst werden, um die Vergleichbarkeit der Messpunkte sicherzustellen!
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Projekt-Name
              </Typography>
              <Typography variant="body1">{projectName}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Beschreibung
              </Typography>
              <Typography variant="body1">
                {projectDescription || <em>Keine Beschreibung</em>}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Ausgewählte Prompts ({selectedPrompts.length})
              </Typography>
              {selectedPrompts.map((prompt) => (
                <Typography key={prompt.id} variant="body2" sx={{ ml: 1 }}>
                  • {prompt.content.length > 100 ? prompt.content.substring(0, 100) + '...' : prompt.content}
                </Typography>
              ))}
            </Box>

            {summaryPlaceholders.size > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Parameter
                </Typography>
                {Array.from(summaryPlaceholders).map((placeholder) => (
                  <Typography key={placeholder} variant="body2" sx={{ ml: 1 }}>
                    • {placeholder}: {placeholderValues[placeholder]}
                  </Typography>
                ))}
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Suchbegriff
              </Typography>
              <Typography variant="body1">
                {searchTerm}
                {(searchTermSynonym1 || searchTermSynonym2) && (
                  <Typography component="span" color="textSecondary">
                    {' '}({[searchTermSynonym1, searchTermSynonym2].filter(Boolean).join(', ')})
                  </Typography>
                )}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              {benchmarkError ? (
                <Alert severity="info">Modellaufstellung konnte nicht geladen werden.</Alert>
              ) : benchmarkModels.length === 0 && !benchmarkLoading ? (
                <Alert severity="warning">Aktuell sind keine Benchmark-Modelle freigegeben.</Alert>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Ausgewertet wird mit den zentral festgelegten Modellen:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {benchmarkModels.map((m) => (
                      <Chip key={m.model_code} label={m.display_name} size="small" />
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={createProjectDialogOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>Neues Projekt erstellen</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Fehler-Anzeige */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Schritt-Inhalt */}
          {renderStepContent()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Abbrechen
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Zurück
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button onClick={handleNext} variant="contained" disabled={!canProceed() || loading}>
            Weiter
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canProceed() || loading}
          >
            {loading ? 'Wird erstellt...' : 'Projekt erstellen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDialog;
